import React, { useState, useEffect, useCallback } from 'react'
import {
  BarChart3,
  TrendingUp,
  Users,
  Server,
  RefreshCw,
  Download,
  Calendar,
  ChevronDown,
  ChevronRight,
  DollarSign,
  Zap,
  Clock,
  Activity,
  AlertCircle,
  Languages,
  ArrowRight,
  Filter
} from 'lucide-react'
import clsx from 'clsx'
import * as adminApi from '../../services/adminApi'
import Toast, { ToastContainer } from '../../components/admin/Toast'

// =============================================================================
// Helper Functions
// =============================================================================

const formatNumber = (num) => {
  if (num === null || num === undefined) return '-'
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`
  return num.toLocaleString()
}

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return '-'
  return `$${amount.toFixed(2)}`
}

const formatPercent = (value) => {
  if (value === null || value === undefined) return '-'
  return `${value.toFixed(1)}%`
}

const formatDate = (dateStr) => {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleDateString()
}

// Format milliseconds to HH:MM:SS (or MM:SS if under 1 hour, or SS.ms if under 1 minute)
const formatDuration = (ms) => {
  if (ms === null || ms === undefined) return '-'

  const totalSeconds = ms / 1000
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = Math.floor(totalSeconds % 60)
  const milliseconds = Math.floor(ms % 1000)

  if (hours > 0) {
    // HH:MM:SS format
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
  } else if (minutes > 0) {
    // MM:SS format
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
  } else {
    // SS.sss format for sub-minute durations
    return `${seconds}.${milliseconds.toString().padStart(3, '0').slice(0, 2)}s`
  }
}

// Predefined date ranges
const dateRanges = [
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 30 days', days: 30 },
  { label: 'Last 90 days', days: 90 },
  { label: 'Custom', days: null }
]

// Provider colors for charts
const providerColors = {
  openai: 'bg-green-500',
  anthropic: 'bg-orange-500',
  gemini: 'bg-blue-500',
  grok: 'bg-purple-500',
  deepseek: 'bg-cyan-500'
}

// =============================================================================
// DateRangePicker Component
// =============================================================================

const DateRangePicker = ({ selectedRange, onRangeChange, customFrom, customTo, onCustomChange }) => {
  const [showCustom, setShowCustom] = useState(selectedRange === null)

  return (
    <div className="flex items-center space-x-4" data-testid="date-range-picker">
      <div className="flex items-center space-x-2">
        <Calendar className="w-4 h-4 text-gray-500" />
        <select
          value={selectedRange || 'custom'}
          onChange={(e) => {
            const value = e.target.value
            if (value === 'custom') {
              setShowCustom(true)
              onRangeChange(null)
            } else {
              setShowCustom(false)
              onRangeChange(parseInt(value))
            }
          }}
          className="text-sm border border-gray-300 rounded-md px-3 py-1.5 focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
          data-testid="date-range-select"
        >
          {dateRanges.map(range => (
            <option key={range.label} value={range.days || 'custom'}>
              {range.label}
            </option>
          ))}
        </select>
      </div>

      {showCustom && (
        <div className="flex items-center space-x-2">
          <input
            type="date"
            value={customFrom}
            onChange={(e) => onCustomChange('from', e.target.value)}
            className="text-sm border border-gray-300 rounded-md px-2 py-1.5 focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
            data-testid="date-from-input"
          />
          <span className="text-gray-500">to</span>
          <input
            type="date"
            value={customTo}
            onChange={(e) => onCustomChange('to', e.target.value)}
            className="text-sm border border-gray-300 rounded-md px-2 py-1.5 focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
            data-testid="date-to-input"
          />
        </div>
      )}
    </div>
  )
}

// =============================================================================
// KPICard Component
// =============================================================================

const KPICard = ({ title, value, subtitle, icon: Icon, trend, color = 'amber' }) => {
  const colorClasses = {
    amber: 'bg-amber-50 text-amber-600',
    green: 'bg-green-50 text-green-600',
    blue: 'bg-blue-50 text-blue-600',
    purple: 'bg-purple-50 text-purple-600'
  }

  return (
    <div className="bg-white rounded-lg shadow p-4" data-testid="kpi-card">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className="text-2xl font-bold mt-1">{value}</p>
          {subtitle && (
            <p className="text-xs text-gray-400 mt-1">{subtitle}</p>
          )}
          {trend !== undefined && (
            <div className={clsx(
              'flex items-center mt-2 text-xs',
              trend >= 0 ? 'text-green-600' : 'text-red-600'
            )}>
              <TrendingUp className={clsx('w-3 h-3 mr-1', trend < 0 && 'rotate-180')} />
              {Math.abs(trend).toFixed(1)}% vs prev period
            </div>
          )}
        </div>
        <div className={clsx('p-2 rounded-lg', colorClasses[color])}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  )
}

// =============================================================================
// ProviderAnalytics Tab Component
// =============================================================================

const ProviderAnalytics = ({ dateFrom, dateTo, days }) => {
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [expandedProviders, setExpandedProviders] = useState({})

  const fetchStats = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await adminApi.getProviderStats({ dateFrom, dateTo, days })
      setStats(data)
    } catch (err) {
      console.error('Failed to fetch provider stats:', err)
      setError(err.message || 'Failed to load provider statistics')
    } finally {
      setLoading(false)
    }
  }, [dateFrom, dateTo, days])

  useEffect(() => {
    fetchStats()
  }, [fetchStats])

  const toggleProvider = (provider) => {
    setExpandedProviders(prev => ({
      ...prev,
      [provider]: !prev[provider]
    }))
  }

  const handleExportCSV = async () => {
    try {
      const response = await adminApi.exportProviderStatsCSV({ dateFrom, dateTo, days })
      const blob = new Blob([response.data], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `provider_stats_${dateFrom || 'last' + days + 'days'}.csv`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (err) {
      console.error('Failed to export CSV:', err)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-amber-500" />
        <span className="ml-2 text-gray-500">Loading provider statistics...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
        <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
        <span className="text-red-700">{error}</span>
      </div>
    )
  }

  if (!stats) return null

  return (
    <div className="space-y-6" data-testid="provider-analytics">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <KPICard
          title="Total Characters"
          value={formatNumber(stats.summary?.total_characters)}
          icon={Activity}
          color="blue"
        />
        <KPICard
          title="Total Requests"
          value={formatNumber(stats.summary?.total_requests)}
          icon={Zap}
          color="purple"
        />
        <KPICard
          title="Total Cost"
          value={formatCurrency(stats.summary?.total_cost_usd)}
          icon={DollarSign}
          color="green"
        />
        <KPICard
          title="Avg Cost/1K Chars"
          value={formatCurrency(stats.summary?.avg_cost_per_1k_chars)}
          icon={TrendingUp}
          color="amber"
        />
      </div>

      {/* Quick Stats */}
      {stats.summary && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              {stats.summary.fastest_provider && (
                <div>
                  <span className="text-sm text-gray-500">Fastest Provider:</span>
                  <span className="ml-2 font-semibold text-amber-700 capitalize">
                    {stats.summary.fastest_provider}
                  </span>
                </div>
              )}
              {stats.summary.best_quality_provider && (
                <div>
                  <span className="text-sm text-gray-500">Best Quality:</span>
                  <span className="ml-2 font-semibold text-amber-700 capitalize">
                    {stats.summary.best_quality_provider}
                  </span>
                </div>
              )}
            </div>
            <button
              onClick={handleExportCSV}
              className="flex items-center px-3 py-1.5 text-sm border border-amber-300 text-amber-700 rounded-md hover:bg-amber-100 transition-colors"
              data-testid="export-provider-csv"
            >
              <Download className="w-4 h-4 mr-1" />
              Export CSV
            </button>
          </div>
        </div>
      )}

      {/* Providers Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200" data-testid="providers-table">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Provider / Account
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Characters
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Requests
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Cost (USD)
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Avg Response
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                P95 Response
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Sec/1K Chars
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Success Rate
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Quality
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {stats.providers?.map((provider) => (
              <React.Fragment key={provider.provider}>
                {/* Provider Row */}
                <tr
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => toggleProvider(provider.provider)}
                  data-testid={`provider-row-${provider.provider}`}
                >
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center">
                      {expandedProviders[provider.provider] ? (
                        <ChevronDown className="w-4 h-4 text-gray-400 mr-2" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-gray-400 mr-2" />
                      )}
                      <div className={clsx(
                        'w-2 h-2 rounded-full mr-2',
                        providerColors[provider.provider] || 'bg-gray-500'
                      )} />
                      <span className="font-medium capitalize">{provider.provider}</span>
                      <span className="ml-2 text-xs text-gray-400">
                        ({provider.accounts?.length || 0} accounts)
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap font-medium">
                    {formatNumber(provider.characters)}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {formatNumber(provider.requests)}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {formatCurrency(provider.cost_usd)}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap font-mono text-sm">
                    {formatDuration(provider.avg_response_ms)}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap font-mono text-sm">
                    {formatDuration(provider.p95_response_ms)}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <span className={clsx(
                      'px-2 py-0.5 rounded text-xs font-medium',
                      provider.seconds_per_1k_chars < 1 ? 'bg-green-100 text-green-800' :
                      provider.seconds_per_1k_chars < 3 ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    )}>
                      {provider.seconds_per_1k_chars?.toFixed(2) || '-'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <span className={clsx(
                      'px-2 py-0.5 rounded text-xs font-medium',
                      provider.success_rate >= 99 ? 'bg-green-100 text-green-800' :
                      provider.success_rate >= 95 ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    )}>
                      {formatPercent(provider.success_rate)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {provider.quality_score ? (
                      <span className={clsx(
                        'px-2 py-0.5 rounded text-xs font-medium',
                        provider.quality_score >= 0.8 ? 'bg-green-100 text-green-800' :
                        provider.quality_score >= 0.6 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      )}>
                        {(provider.quality_score * 100).toFixed(0)}
                      </span>
                    ) : '-'}
                  </td>
                </tr>

                {/* Account Rows (expanded) */}
                {expandedProviders[provider.provider] && provider.accounts?.map((account) => (
                  <tr
                    key={account.account_id}
                    className="bg-gray-50 text-sm"
                    data-testid={`account-row-${account.account_id}`}
                  >
                    <td className="px-4 py-2 pl-12 whitespace-nowrap">
                      <div>
                        <span className="text-gray-700">{account.account_name}</span>
                        <span className="ml-2 text-gray-400">({account.model_name})</span>
                      </div>
                    </td>
                    <td className="px-4 py-2 text-right whitespace-nowrap text-gray-600">
                      {formatNumber(account.characters)}
                    </td>
                    <td className="px-4 py-2 text-right whitespace-nowrap text-gray-600">
                      {formatNumber(account.requests)}
                    </td>
                    <td className="px-4 py-2 text-right whitespace-nowrap text-gray-600">
                      {formatCurrency(account.cost_usd)}
                    </td>
                    <td className="px-4 py-2 text-right whitespace-nowrap text-gray-600 font-mono text-xs">
                      {formatDuration(account.avg_response_ms)}
                    </td>
                    <td className="px-4 py-2 text-right whitespace-nowrap text-gray-600 font-mono text-xs">
                      {formatDuration(account.p95_response_ms)}
                    </td>
                    <td className="px-4 py-2 text-right whitespace-nowrap text-gray-600">
                      {account.seconds_per_1k_chars?.toFixed(2) || '-'}
                    </td>
                    <td className="px-4 py-2 text-right whitespace-nowrap text-gray-600">
                      {formatPercent(account.success_rate)}
                    </td>
                    <td className="px-4 py-2 text-right whitespace-nowrap text-gray-600">
                      {account.quality_score ? (account.quality_score * 100).toFixed(0) : '-'}
                    </td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>

        {(!stats.providers || stats.providers.length === 0) && (
          <div className="text-center py-8 text-gray-500">
            No provider data available for the selected period
          </div>
        )}
      </div>
    </div>
  )
}

// =============================================================================
// OverviewDashboard Tab Component
// =============================================================================

const OverviewDashboard = ({ dateFrom, dateTo, days }) => {
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchStats = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await adminApi.getOverviewStats({ dateFrom, dateTo, days })
      setStats(data)
    } catch (err) {
      console.error('Failed to fetch overview stats:', err)
      setError(err.message || 'Failed to load overview statistics')
    } finally {
      setLoading(false)
    }
  }, [dateFrom, dateTo, days])

  useEffect(() => {
    fetchStats()
  }, [fetchStats])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-amber-500" />
        <span className="ml-2 text-gray-500">Loading overview statistics...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
        <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
        <span className="text-red-700">{error}</span>
      </div>
    )
  }

  if (!stats) return null

  return (
    <div className="space-y-6" data-testid="overview-dashboard">
      {/* User KPIs */}
      <div>
        <h3 className="text-lg font-medium mb-4">User Metrics</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <KPICard
            title="Total Users"
            value={formatNumber(stats.users?.total)}
            icon={Users}
            color="blue"
          />
          <KPICard
            title="Active Users"
            value={formatNumber(stats.users?.active)}
            subtitle="with requests in period"
            icon={Activity}
            color="green"
          />
          <KPICard
            title="New Users"
            value={formatNumber(stats.users?.new_this_period)}
            subtitle="joined this period"
            icon={TrendingUp}
            color="purple"
          />
          <KPICard
            title="MAU"
            value={formatNumber(stats.users?.mau)}
            subtitle="monthly active users"
            icon={Users}
            color="amber"
          />
        </div>
      </div>

      {/* Usage KPIs */}
      <div>
        <h3 className="text-lg font-medium mb-4">Usage Metrics</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <KPICard
            title="Total Characters"
            value={formatNumber(stats.usage?.total_characters)}
            icon={Activity}
            color="blue"
          />
          <KPICard
            title="Translation Chars"
            value={formatNumber(stats.usage?.translation_characters)}
            icon={Zap}
            color="green"
          />
          <KPICard
            title="Review Chars"
            value={formatNumber(stats.usage?.review_characters)}
            icon={BarChart3}
            color="purple"
          />
          <KPICard
            title="Total Requests"
            value={formatNumber(stats.usage?.total_requests)}
            icon={Server}
            color="amber"
          />
        </div>
      </div>

      {/* Financial KPIs */}
      <div>
        <h3 className="text-lg font-medium mb-4">Financial Metrics</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <KPICard
            title="API Costs"
            value={formatCurrency(stats.financial?.api_costs_usd)}
            icon={DollarSign}
            color="blue"
          />
          <KPICard
            title="Revenue"
            value={formatCurrency(stats.financial?.revenue_usd)}
            subtitle="in period"
            icon={TrendingUp}
            color="green"
          />
          <KPICard
            title="MRR"
            value={formatCurrency(stats.financial?.mrr_usd)}
            subtitle="monthly recurring"
            icon={DollarSign}
            color="purple"
          />
          <KPICard
            title="Gross Margin"
            value={formatPercent(stats.financial?.gross_margin_percent)}
            icon={BarChart3}
            color="amber"
          />
        </div>
      </div>

      {/* Distributions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Users by Plan */}
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-lg font-medium mb-4">Users by Plan</h3>
          <div className="space-y-3" data-testid="users-by-plan">
            {Object.entries(stats.distributions?.users_by_plan || {}).map(([plan, count]) => (
              <div key={plan} className="flex items-center justify-between">
                <span className="text-gray-600 capitalize">{plan}</span>
                <div className="flex items-center">
                  <div className="w-32 h-2 bg-gray-200 rounded-full mr-3">
                    <div
                      className="h-full bg-amber-500 rounded-full"
                      style={{
                        width: `${(count / (stats.users?.total || 1)) * 100}%`
                      }}
                    />
                  </div>
                  <span className="font-medium w-12 text-right">{count}</span>
                </div>
              </div>
            ))}
            {Object.keys(stats.distributions?.users_by_plan || {}).length === 0 && (
              <p className="text-gray-400 text-sm">No plan data available</p>
            )}
          </div>
        </div>

        {/* Requests by Type */}
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-lg font-medium mb-4">Requests by Type</h3>
          <div className="space-y-3" data-testid="requests-by-type">
            {Object.entries(stats.distributions?.requests_by_type || {}).map(([type, count]) => (
              <div key={type} className="flex items-center justify-between">
                <span className="text-gray-600 capitalize">{type || 'Unknown'}</span>
                <div className="flex items-center">
                  <div className="w-32 h-2 bg-gray-200 rounded-full mr-3">
                    <div
                      className="h-full bg-blue-500 rounded-full"
                      style={{
                        width: `${(count / (stats.usage?.total_requests || 1)) * 100}%`
                      }}
                    />
                  </div>
                  <span className="font-medium w-16 text-right">{formatNumber(count)}</span>
                </div>
              </div>
            ))}
            {Object.keys(stats.distributions?.requests_by_type || {}).length === 0 && (
              <p className="text-gray-400 text-sm">No request data available</p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// =============================================================================
// UserAnalytics Tab Component
// =============================================================================

const UserAnalytics = ({ dateFrom, dateTo, days }) => {
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [sortBy, setSortBy] = useState('characters_used')
  const [order, setOrder] = useState('desc')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const limit = 25

  const fetchStats = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await adminApi.getUserStats({
        dateFrom,
        dateTo,
        days,
        sortBy,
        order,
        limit,
        offset: page * limit,
        search: search || null
      })
      setStats(data)
    } catch (err) {
      console.error('Failed to fetch user stats:', err)
      setError(err.message || 'Failed to load user statistics')
    } finally {
      setLoading(false)
    }
  }, [dateFrom, dateTo, days, sortBy, order, page, search])

  useEffect(() => {
    fetchStats()
  }, [fetchStats])

  const handleSort = (column) => {
    if (sortBy === column) {
      setOrder(order === 'desc' ? 'asc' : 'desc')
    } else {
      setSortBy(column)
      setOrder('desc')
    }
    setPage(0)
  }

  const handleExportCSV = async () => {
    try {
      const response = await adminApi.exportUserStatsCSV({ dateFrom, dateTo, days })
      const blob = new Blob([response.data], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `user_stats_${dateFrom || 'last' + days + 'days'}.csv`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (err) {
      console.error('Failed to export CSV:', err)
    }
  }

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-amber-500" />
        <span className="ml-2 text-gray-500">Loading user statistics...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
        <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
        <span className="text-red-700">{error}</span>
      </div>
    )
  }

  return (
    <div className="space-y-6" data-testid="user-analytics">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <KPICard
          title="Total Users"
          value={formatNumber(stats?.summary?.total_users)}
          icon={Users}
          color="blue"
        />
        <KPICard
          title="New Users"
          value={formatNumber(stats?.summary?.new_users)}
          subtitle="in period"
          icon={TrendingUp}
          color="green"
        />
        <KPICard
          title="At 80%+ Quota"
          value={formatNumber(stats?.summary?.users_at_80_percent_quota)}
          subtitle="high usage users"
          icon={AlertCircle}
          color="purple"
        />
        <KPICard
          title="Avg Revenue/User"
          value={formatCurrency(stats?.summary?.avg_revenue_per_user)}
          icon={DollarSign}
          color="amber"
        />
      </div>

      {/* Search and Export */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <input
            type="text"
            placeholder="Search by email..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              setPage(0)
            }}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
            data-testid="user-search-input"
          />
          <span className="text-sm text-gray-500" data-testid="users-count">
            Showing {stats?.users?.length || 0} of {stats?.pagination?.total || 0} users
          </span>
        </div>
        <button
          onClick={handleExportCSV}
          className="flex items-center px-3 py-1.5 text-sm border border-amber-300 text-amber-700 rounded-md hover:bg-amber-100 transition-colors"
          data-testid="export-users-csv"
        >
          <Download className="w-4 h-4 mr-1" />
          Export CSV
        </button>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200" data-testid="users-table">
          <thead className="bg-gray-50">
            <tr>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('email')}
              >
                Email {sortBy === 'email' && (order === 'desc' ? '↓' : '↑')}
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('plan')}
              >
                Plan {sortBy === 'plan' && (order === 'desc' ? '↓' : '↑')}
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th
                className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('characters_used')}
              >
                Characters {sortBy === 'characters_used' && (order === 'desc' ? '↓' : '↑')}
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Quota %
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Cost Generated
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Revenue
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Last Active
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {stats?.users?.map((user) => (
              <tr key={user.id} className="hover:bg-gray-50" data-testid={`user-row-${user.id}`}>
                <td className="px-4 py-3 whitespace-nowrap">
                  <span className="text-sm font-medium text-gray-900">{user.email}</span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <span className={clsx(
                    'px-2 py-0.5 text-xs rounded-full',
                    user.plan === 'enterprise' ? 'bg-purple-100 text-purple-800' :
                    user.plan === 'pro' ? 'bg-blue-100 text-blue-800' :
                    user.plan === 'starter' ? 'bg-green-100 text-green-800' :
                    'bg-gray-100 text-gray-800'
                  )}>
                    {user.plan}
                  </span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <span className={clsx(
                    'px-2 py-0.5 text-xs rounded-full',
                    user.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  )}>
                    {user.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap text-sm text-gray-600">
                  {formatNumber(user.characters_used)}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <span className={clsx(
                    'px-2 py-0.5 text-xs rounded',
                    user.quota_percent >= 90 ? 'bg-red-100 text-red-800' :
                    user.quota_percent >= 80 ? 'bg-orange-100 text-orange-800' :
                    user.quota_percent >= 50 ? 'bg-yellow-100 text-yellow-800' :
                    'bg-green-100 text-green-800'
                  )}>
                    {formatPercent(user.quota_percent)}
                  </span>
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap text-sm text-gray-600">
                  {formatCurrency(user.cost_generated_usd)}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap text-sm text-gray-600">
                  {formatCurrency(user.revenue_usd)}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap text-sm text-gray-400">
                  {user.last_active ? formatDate(user.last_active) : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {(!stats?.users || stats.users.length === 0) && (
          <div className="text-center py-8 text-gray-500">
            No users found for the selected criteria
          </div>
        )}

        {/* Pagination */}
        {stats?.pagination && stats.pagination.total > limit && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
            <button
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
              className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Previous
            </button>
            <span className="text-sm text-gray-500">
              Page {page + 1} of {Math.ceil(stats.pagination.total / limit)}
            </span>
            <button
              onClick={() => setPage(p => p + 1)}
              disabled={(page + 1) * limit >= stats.pagination.total}
              className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

// =============================================================================
// LanguagePairAnalytics Tab Component
// =============================================================================

const LanguagePairAnalytics = ({ dateFrom, dateTo, days }) => {
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedProvider, setSelectedProvider] = useState('')
  const [selectedSourceLang, setSelectedSourceLang] = useState('')
  const [sortBy, setSortBy] = useState('characters')
  const [order, setOrder] = useState('desc')
  const [page, setPage] = useState(0)
  const limit = 50

  const fetchStats = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await adminApi.getLanguagePairStats({
        dateFrom,
        dateTo,
        days,
        provider: selectedProvider || null,
        sourceLang: selectedSourceLang || null,
        sortBy,
        order,
        limit,
        offset: page * limit
      })
      setStats(data)
    } catch (err) {
      console.error('Failed to fetch language pair stats:', err)
      setError(err.message || 'Failed to load language pair statistics')
    } finally {
      setLoading(false)
    }
  }, [dateFrom, dateTo, days, selectedProvider, selectedSourceLang, sortBy, order, page])

  useEffect(() => {
    fetchStats()
  }, [fetchStats])

  const handleSort = (column) => {
    if (sortBy === column) {
      setOrder(order === 'desc' ? 'asc' : 'desc')
    } else {
      setSortBy(column)
      setOrder('desc')
    }
    setPage(0)
  }

  const handleExportCSV = async () => {
    try {
      const response = await adminApi.exportLanguagePairStatsCSV({
        dateFrom,
        dateTo,
        days,
        provider: selectedProvider || null
      })
      const blob = new Blob([response.data], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `language_pair_stats_${dateFrom || 'last' + days + 'days'}.csv`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (err) {
      console.error('Failed to export CSV:', err)
    }
  }

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-amber-500" />
        <span className="ml-2 text-gray-500">Loading language pair statistics...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
        <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
        <span className="text-red-700">{error}</span>
      </div>
    )
  }

  return (
    <div className="space-y-6" data-testid="language-pair-analytics">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <KPICard
          title="Language Pairs"
          value={formatNumber(stats?.summary?.total_language_pairs)}
          icon={Languages}
          color="blue"
        />
        <KPICard
          title="Total Characters"
          value={formatNumber(stats?.summary?.total_characters)}
          icon={Activity}
          color="green"
        />
        <KPICard
          title="Total Requests"
          value={formatNumber(stats?.summary?.total_requests)}
          icon={Zap}
          color="purple"
        />
        <KPICard
          title="Total Cost"
          value={formatCurrency(stats?.summary?.total_cost_usd)}
          icon={DollarSign}
          color="amber"
        />
      </div>

      {/* Filters and Export */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <select
              value={selectedProvider}
              onChange={(e) => {
                setSelectedProvider(e.target.value)
                setPage(0)
              }}
              className="text-sm border border-gray-300 rounded-md px-3 py-1.5 focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              data-testid="provider-filter"
            >
              <option value="">All Providers</option>
              {stats?.filters?.providers?.map(p => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>
          <select
            value={selectedSourceLang}
            onChange={(e) => {
              setSelectedSourceLang(e.target.value)
              setPage(0)
            }}
            className="text-sm border border-gray-300 rounded-md px-3 py-1.5 focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
            data-testid="source-lang-filter"
          >
            <option value="">All Source Languages</option>
            {stats?.filters?.source_languages?.map(lang => (
              <option key={lang} value={lang}>{lang}</option>
            ))}
          </select>
          <span className="text-sm text-gray-500" data-testid="pairs-count">
            Showing {stats?.language_pairs?.length || 0} of {stats?.pagination?.total || 0} pairs
          </span>
        </div>
        <button
          onClick={handleExportCSV}
          className="flex items-center px-3 py-1.5 text-sm border border-amber-300 text-amber-700 rounded-md hover:bg-amber-100 transition-colors"
          data-testid="export-language-pairs-csv"
        >
          <Download className="w-4 h-4 mr-1" />
          Export CSV
        </button>
      </div>

      {/* Language Pairs Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200" data-testid="language-pairs-table">
            <thead className="bg-gray-50">
              <tr>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('provider')}
                >
                  Provider {sortBy === 'provider' && (order === 'desc' ? '↓' : '↑')}
                </th>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('language_pair')}
                >
                  Language Pair {sortBy === 'language_pair' && (order === 'desc' ? '↓' : '↑')}
                </th>
                <th
                  className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('characters')}
                >
                  Characters {sortBy === 'characters' && (order === 'desc' ? '↓' : '↑')}
                </th>
                <th
                  className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('requests')}
                >
                  Requests {sortBy === 'requests' && (order === 'desc' ? '↓' : '↑')}
                </th>
                <th
                  className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('cost_usd')}
                >
                  Cost (USD) {sortBy === 'cost_usd' && (order === 'desc' ? '↓' : '↑')}
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Avg Response
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  P95 Response
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sec/1K Chars
                </th>
                <th
                  className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('success_rate')}
                >
                  Success % {sortBy === 'success_rate' && (order === 'desc' ? '↓' : '↑')}
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Users
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Used
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {stats?.language_pairs?.map((pair, index) => (
                <tr
                  key={`${pair.provider}-${pair.language_pair}-${index}`}
                  className="hover:bg-gray-50"
                  data-testid={`pair-row-${pair.language_pair.replace('->', '-')}`}
                >
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={clsx(
                      'px-2 py-0.5 text-xs rounded-full font-medium',
                      pair.provider === 'openai' ? 'bg-green-100 text-green-800' :
                      pair.provider === 'anthropic' ? 'bg-orange-100 text-orange-800' :
                      pair.provider === 'gemini' ? 'bg-blue-100 text-blue-800' :
                      pair.provider === 'grok' ? 'bg-purple-100 text-purple-800' :
                      'bg-gray-100 text-gray-800'
                    )}>
                      {pair.provider}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center">
                      <span className="font-medium">{pair.source_lang}</span>
                      <ArrowRight className="w-3 h-3 mx-1 text-gray-400" />
                      <span className="font-medium">{pair.target_lang}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap font-medium">
                    {formatNumber(pair.characters)}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {formatNumber(pair.requests)}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {formatCurrency(pair.cost_usd)}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap font-mono text-sm">
                    {formatDuration(pair.avg_response_ms)}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap font-mono text-sm">
                    {formatDuration(pair.p95_response_ms)}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <span className={clsx(
                      'px-2 py-0.5 rounded text-xs font-medium',
                      pair.seconds_per_1k_chars < 1 ? 'bg-green-100 text-green-800' :
                      pair.seconds_per_1k_chars < 3 ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    )}>
                      {pair.seconds_per_1k_chars?.toFixed(2) || '-'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <span className={clsx(
                      'px-2 py-0.5 rounded text-xs font-medium',
                      pair.success_rate >= 99 ? 'bg-green-100 text-green-800' :
                      pair.success_rate >= 95 ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    )}>
                      {formatPercent(pair.success_rate)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap text-sm text-gray-600">
                    {pair.unique_users}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap text-sm text-gray-400">
                    {pair.last_used ? formatDate(pair.last_used.split('T')[0]) : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {(!stats?.language_pairs || stats.language_pairs.length === 0) && (
          <div className="text-center py-8 text-gray-500">
            No language pair data available for the selected criteria
          </div>
        )}

        {/* Pagination */}
        {stats?.pagination && stats.pagination.total > limit && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
            <button
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
              className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Previous
            </button>
            <span className="text-sm text-gray-500">
              Page {page + 1} of {Math.ceil(stats.pagination.total / limit)}
            </span>
            <button
              onClick={() => setPage(p => p + 1)}
              disabled={(page + 1) * limit >= stats.pagination.total}
              className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

// =============================================================================
// Main StatisticsPage Component
// =============================================================================

const StatisticsPage = () => {
  // Tab state (Provider Analytics is default as per user request)
  const [activeTab, setActiveTab] = useState('providers')

  // Date range state
  const [selectedDays, setSelectedDays] = useState(30)
  const [customFrom, setCustomFrom] = useState('')
  const [customTo, setCustomTo] = useState('')

  // Toast state
  const [toasts, setToasts] = useState([])

  const addToast = useCallback((message, type = 'info') => {
    const id = Date.now()
    setToasts(prev => [...prev, { id, message, type }])
  }, [])

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id))
  }, [])

  const handleRangeChange = (days) => {
    setSelectedDays(days)
    if (days !== null) {
      setCustomFrom('')
      setCustomTo('')
    }
  }

  const handleCustomChange = (field, value) => {
    if (field === 'from') {
      setCustomFrom(value)
    } else {
      setCustomTo(value)
    }
  }

  // Determine date params for API calls
  const dateParams = selectedDays
    ? { days: selectedDays }
    : { dateFrom: customFrom, dateTo: customTo }

  const tabs = [
    { id: 'providers', label: 'Provider Analytics', icon: Server },
    { id: 'language-pairs', label: 'Language Pairs', icon: Languages },
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'users', label: 'User Analytics', icon: Users }
  ]

  return (
    <div className="space-y-6" data-testid="statistics-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Statistics</h1>
          <p className="text-sm text-gray-500 mt-1">Usage analytics and performance metrics</p>
        </div>
        <DateRangePicker
          selectedRange={selectedDays}
          onRangeChange={handleRangeChange}
          customFrom={customFrom}
          customTo={customTo}
          onCustomChange={handleCustomChange}
        />
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8" data-testid="statistics-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={clsx(
                'flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors',
                activeTab === tab.id
                  ? 'border-amber-500 text-amber-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              )}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4 mr-2" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'providers' && (
          <ProviderAnalytics {...dateParams} />
        )}
        {activeTab === 'language-pairs' && (
          <LanguagePairAnalytics {...dateParams} />
        )}
        {activeTab === 'overview' && (
          <OverviewDashboard {...dateParams} />
        )}
        {activeTab === 'users' && (
          <UserAnalytics {...dateParams} />
        )}
      </div>

      {/* Toast Container */}
      <ToastContainer>
        {toasts.map(toast => (
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </ToastContainer>
    </div>
  )
}

export default StatisticsPage
